import {
  ENEMY_SPAWN_TUNING,
  GAME_HEIGHT,
  PLAYER_HEIGHT,
  PLAYER_WIDTH,
  PLAYER_X,
  getExpToNextLevel,
} from "./data/constants"
import { SHOP_UPGRADES, UPGRADES } from "./data/upgrades"
import type {
  BumperEffectType,
  Character,
  ExpCoin,
  ItemUpgrade,
  Player,
  PlayerStats,
  StatUpgrade,
  Upgrade,
} from "./types"

export function normalizeAssetRelativePath(assetPath: string): string {
  const normalized = assetPath
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/^assets\//, "")
    .split("/")
    .filter(Boolean)
    .join("/")

  if (!normalized) {
    throw new Error("Asset path is required")
  }

  return normalized
}

export function toPublicAssetUrl(assetPath: string): string {
  return `/assets/${normalizeAssetRelativePath(assetPath)}`
}

export function clampEnemyY(y: number): number {
  return Math.max(ENEMY_SPAWN_TUNING.topMargin, Math.min(GAME_HEIGHT - 40, y))
}

let idCounter = 0
const generateId = (): string => `${Date.now()}-${idCounter++}`

const PLAYER_DEFAULTS = {
  initialScoreMultiplier: 1,
} as const

const DEFAULT_PLAYER_PASSIVES = {
  waveGuardPerWave: 0,
  comboDecayGracePeriod: 0,
  rerollCharges: 0,
  coinMultiplier: 0,
  expMultiplier: 0,
  scoreMultiplier: 0,
  combFillSpeedMultiplier: 0,
  damageMultiplier: 0,
  speedMultiplier: 0,
  hpMultiplier: 0,
  wavesSkipped: 0,
  magnetRadiusMultiplier: 0,
  magnetSpeedMultiplier: 0,
  overdriveDurationMultiplier: 0,
} as const

const PLAYER_LIMITS = {
  minimumMaxHp: 1,
  minimumCurrentHp: 0,
} as const

export const createPlayer = (character: Character): Player => {
  return {
    x: PLAYER_X,
    y: GAME_HEIGHT / 2,
    vy: 0,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
    hp: character.maxHp,
    maxHp: character.maxHp,
    stats: {
      maxHp: character.maxHp,
      attackSpeed: 1 + (character.baseStats?.attackSpeed || 0),
      ballBounces: character.baseStats?.ballBounces || 0,
      ballCount: character.baseStats?.ballCount || 1,
      ballSize: character.baseStats?.ballSize || 1.0,
      flapPower: character.baseStats?.flapPower || 1.0,
    },
    statLevels: {
      hp: 0,
      attackSpeed: 0,
      ballBounces: 0,
      ballCount: 0,
      ballSize: 0,
      flapPower: 0,
    },
    exp: 0,
    expToLevel: getExpToNextLevel(1),
    level: 1,
    isInvulnerable: false,
    invulnerabilityTimer: 0,
    scoreMultiplier: PLAYER_DEFAULTS.initialScoreMultiplier,
    powerMode: null,
    powerTimer: 0,
    items: [],
    bumperWeights: {
      charge: 1,
      guard: 0,
      bank: 1,
      heal: 1,
      levelup: 1,
    },
    passives: {
      ...DEFAULT_PLAYER_PASSIVES,
      ...character.basePassives,
    },
    waveGuardCharges: 0,
    rerollCharges: 0,
    combo: {
      state: "normal" as const,
      hits: 0,
      targetHits: 5,
      nextEffect: "charge" as const,
      timer: 0,
      hitColors: [] as string[],
    },
    usedRevives: [],
  }
}

export const createExpCoin = (
  x: number,
  y: number,
  value: number,
): ExpCoin => ({
  id: generateId(),
  x,
  y,
  value,
  size: Math.min(8 + value / 5, 20),
})

const isUpgradeOwned = (player: Player, upgrade: Upgrade): boolean => {
  return player.items.some((item) => item.id === upgrade.id)
}

const isStatUpgrade = (upgrade: Upgrade): upgrade is StatUpgrade => {
  return upgrade.type === "stat"
}

const isItemUpgrade = (upgrade: Upgrade): upgrade is ItemUpgrade => {
  return upgrade.type === "item"
}

const isInventoryItem = (upgrade: ItemUpgrade): boolean => {
  return !upgrade.consumable
}

const supportsOfferSource = (
  upgrade: Upgrade,
  source: "level" | "shop",
): boolean => {
  if (!upgrade.offerSource || upgrade.offerSource === "both") return true
  return upgrade.offerSource === source
}

export const isUpgradeAvailableForSource = (
  player: Player,
  upgrade: Upgrade,
  source: "level" | "shop",
): boolean => {
  if (!supportsOfferSource(upgrade, source)) return false
  if (!upgrade.repeatable && isUpgradeOwned(player, upgrade)) return false
  return true
}

export const getRandomUpgrades = (
  count: number,
  player: Player,
  source: "level" | "shop" = "level",
): Upgrade[] => {
  const availableUpgrades = (
    source === "shop" ? SHOP_UPGRADES : UPGRADES
  ).filter((upgrade: Upgrade) =>
    isUpgradeAvailableForSource(player, upgrade, source),
  )

  if (availableUpgrades.length <= count) return availableUpgrades

  const shuffled = [...availableUpgrades].sort(() => Math.random() - 0.5)

  if (source === "level") {
    // For level-up, we want variety in the stat types offered (e.g. don't show 3 HP boosts)
    const result: Upgrade[] = []
    const seenStats = new Set<string>()

    for (const upgrade of shuffled) {
      if (result.length >= count) break

      if (isStatUpgrade(upgrade) && upgrade.statBoost) {
        // Identify the primary stat boosted (the first key in statBoost)
        const primaryStat = Object.keys(upgrade.statBoost)[0]
        if (!seenStats.has(primaryStat)) {
          result.push(upgrade)
          seenStats.add(primaryStat)
        }
      } else {
        result.push(upgrade)
      }
    }

    // Fallback: if we didn't find enough unique stat types, fill with whatever is left
    if (result.length < count) {
      for (const upgrade of shuffled) {
        if (result.length >= count) break
        if (!result.includes(upgrade)) {
          result.push(upgrade)
        }
      }
    }
    return result
  }

  return shuffled.slice(0, count)
}

export const applyUpgrade = (player: Player, upgrade: Upgrade): Player => {
  const newPlayer = { ...player }

  if (isStatUpgrade(upgrade) && upgrade.statBoost) {
    const newStats: PlayerStats = { ...newPlayer.stats }
    const newStatLevels = { ...newPlayer.statLevels }

    for (const key of Object.keys(upgrade.statBoost)) {
      const statKey = key as keyof PlayerStats
      // Determine stat name for statLevels tracking
      let levelKey = ""
      if (statKey === "maxHp") levelKey = "hp"
      else if (statKey === "attackSpeed") levelKey = "attackSpeed"
      else if (statKey === "ballBounces") levelKey = "ballBounces"
      else if (statKey === "ballCount") levelKey = "ballCount"
      else if (statKey === "ballSize") levelKey = "ballSize"
      else if (statKey === "flapPower") levelKey = "flapPower"

      if (levelKey) {
        newStatLevels[levelKey] += 1
        const level = newStatLevels[levelKey]

        if (statKey === "maxHp") {
          let boostRatio = 0
          if (level === 1) boostRatio = 0.8
          else if (level === 2) boostRatio = 0.5
          else if (level === 3) boostRatio = 0.25
          else boostRatio = 0.1 // Baseline for beyond Lv 3

          const boost = Math.floor(newStats.maxHp * boostRatio)
          newStats.maxHp += boost
          newPlayer.hp += boost
          newPlayer.maxHp = newStats.maxHp
        } else if (statKey === "ballSize") {
          newStats.ballSize += 0.1
        } else if (statKey === "ballCount") {
          newStats.ballCount += 1
        } else if (statKey === "ballBounces") {
          newStats.ballBounces += 1
        } else if (statKey === "attackSpeed") {
          newStats.attackSpeed += 0.1
        } else if (statKey === "flapPower") {
          newStats.flapPower += 0.1
        }
      }
    }

    newPlayer.stats = newStats
    newPlayer.statLevels = newStatLevels
  }

  if (isItemUpgrade(upgrade) && upgrade.bumperWeightBoost) {
    const bumperWeights: Record<BumperEffectType, number> = {
      ...newPlayer.bumperWeights,
    }

    for (const [key, value] of Object.entries(upgrade.bumperWeightBoost)) {
      const bumperKey = key as BumperEffectType
      bumperWeights[bumperKey] = Math.max(
        0.1,
        (bumperWeights[bumperKey] ?? 1) + (value ?? 0),
      )
    }

    newPlayer.bumperWeights = bumperWeights
  }

  if (isItemUpgrade(upgrade) && upgrade.passiveEffect) {
    newPlayer.passives = {
      ...newPlayer.passives,
      ...Object.fromEntries(
        Object.entries(upgrade.passiveEffect).map(([key, value]) => {
          const passiveKey = key as keyof typeof DEFAULT_PLAYER_PASSIVES
          return [
            passiveKey,
            (newPlayer.passives[passiveKey] ?? 0) + (value ?? 0),
          ]
        }),
      ),
    }
    newPlayer.waveGuardCharges = Math.max(
      newPlayer.waveGuardCharges,
      newPlayer.passives.waveGuardPerWave,
    )

    // Apply HP multiplier from relics (Reinforced Armor +40%, Glass Cannon -30%)
    if (upgrade.passiveEffect?.hpMultiplier) {
      const hpMultiplier = upgrade.passiveEffect.hpMultiplier
      const hpDelta = Math.floor(newPlayer.maxHp * hpMultiplier)
      newPlayer.maxHp = Math.max(
        PLAYER_LIMITS.minimumMaxHp,
        newPlayer.maxHp + hpDelta,
      )
      newPlayer.hp = Math.max(
        PLAYER_LIMITS.minimumCurrentHp,
        newPlayer.hp + hpDelta,
      )
    }
  }

  if (isItemUpgrade(upgrade) && (upgrade.healAmount || upgrade.healFraction)) {
    const healFromAmount = upgrade.healAmount ?? 0
    const healFromFraction = Math.floor(
      newPlayer.maxHp * (upgrade.healFraction ?? 0),
    )
    const healTotal = Math.max(0, healFromAmount + healFromFraction)
    newPlayer.hp = Math.min(newPlayer.maxHp, newPlayer.hp + healTotal)
  }

  if (isItemUpgrade(upgrade) && isInventoryItem(upgrade)) {
    newPlayer.items = [...newPlayer.items, upgrade]
  }

  return newPlayer
}

export const removeOwnedItemUpgrade = (
  player: Player,
  upgrade: ItemUpgrade,
): Player => {
  if (!isInventoryItem(upgrade)) {
    return player
  }

  const ownedItemIndex = player.items.findLastIndex(
    (item) => item.id === upgrade.id,
  )
  if (ownedItemIndex < 0) {
    return player
  }

  const newPlayer: Player = {
    ...player,
    items: player.items.filter((_, index) => index !== ownedItemIndex),
  }

  if (upgrade.bumperWeightBoost) {
    const bumperWeights: Record<BumperEffectType, number> = {
      ...newPlayer.bumperWeights,
    }

    for (const [key, value] of Object.entries(upgrade.bumperWeightBoost)) {
      const bumperKey = key as BumperEffectType
      bumperWeights[bumperKey] = Math.max(
        0.1,
        (bumperWeights[bumperKey] ?? 1) - (value ?? 0),
      )
    }

    newPlayer.bumperWeights = bumperWeights
  }

  if (upgrade.passiveEffect) {
    newPlayer.passives = {
      ...newPlayer.passives,
      ...Object.fromEntries(
        Object.entries(upgrade.passiveEffect).map(([key, value]) => {
          const passiveKey = key as keyof typeof DEFAULT_PLAYER_PASSIVES
          return [
            passiveKey,
            (newPlayer.passives[passiveKey] ?? 0) - (value ?? 0),
          ]
        }),
      ),
    }

    newPlayer.waveGuardCharges = Math.min(
      newPlayer.waveGuardCharges,
      newPlayer.passives.waveGuardPerWave,
    )
  }

  return newPlayer
}

/**
 * Check if player has a viable revive item and revive them if so.
 * Returns true if revived, false otherwise.
 * Marks the revive as used so it won't trigger twice.
 */
export const tryRevivePlayer = (
  player: Player,
  isBossDeath = false,
): boolean => {
  // Phoenix Revival: works on any death (25% HP)
  const phoenixItem = player.items.find(
    (item) => item.id === "item-phoenix-revival",
  ) as ItemUpgrade | undefined
  if (
    phoenixItem &&
    !player.usedRevives.includes("item-phoenix-revival") &&
    phoenixItem.healFraction
  ) {
    const healAmount = Math.ceil(player.maxHp * phoenixItem.healFraction)
    player.hp = Math.max(0.1, healAmount)
    player.usedRevives = [...player.usedRevives, "item-phoenix-revival"]
    return true
  }

  // Second Wind: only works on boss deaths (40% HP)
  if (isBossDeath && !player.usedRevives.includes("item-second-wind")) {
    const secondWindItem = player.items.find(
      (item) => item.id === "item-second-wind",
    ) as ItemUpgrade | undefined
    if (secondWindItem?.healFraction) {
      const healAmount = Math.ceil(player.maxHp * secondWindItem.healFraction)
      player.hp = Math.max(0.1, healAmount)
      player.usedRevives = [...player.usedRevives, "item-second-wind"]
      return true
    }
  }

  return false
}
